/* eslint no-unused-vars: "off" */
import { getUserState } from '@carvana/core-auth';
import axiosWithAuth, { validateAuthResponse } from '../utils/axiosWithAuth';

const { isLoggedIn } = getUserState();

const MY_CARS_CANCEL_PAYMENT_API_URI: string =
  '/cltv/loanservicing/api/v1/payment/cancel/onetime/vehicleid/';
const MY_CARS_API_URI: string = '/cltv/mycars/api/v3/purchases';
const MY_CARS_LOAN_API_URI: string =
  '/cltv/loanservicing/api/v2/loan/details/vehicleId/';
const MY_CARS_PAY_METHOD_API_URI: string =
  '/cltv/loanservicing/api/v1/paymentmethod/list/vehicleId/';
const MY_CARS_PAY_METHOD_ADD_API_URI: string =
  '/cltv/loanservicing/api/v1/paymentmethod/add/vehicleid/';
const MY_CARS_ONE_TIME_PAYMENT_SCHEDULE_API_URI: string =
  '/cltv/loanservicing/api/v1/payment/schedule/onetime/vehicleid/';
const MY_CARS_CONTACT_DETAILS_API_URI: string =
  '/core/account-api/api/v1/personaldetails';
const MY_CARS_SCHEDULE_AUTOPAY_METHOD_API_URI: string =
  '/cltv/loanservicing/api/v1/payment/schedule/recurring/vehicleid/';
const MY_CARS_DISABLE_AUTOPAY_API_URI: string =
  '/cltv/loanservicing/api/v1/payment/cancel/recurring/vehicleid';
const MY_CARS_TRANSACTION_HISTORY_API_URI: string =
  '/cltv/loanservicing/api/v1/transactionhistory/vehicleId/';
const SEVERITY_LEVEL_ERROR: number = 4;

// POST Calls

const scheduleOneTimePayment = (
  env: string,
  vehicleId: string,
  paymentDetails: {},
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_ONE_TIME_PAYMENT_SCHEDULE_API_URI}${vehicleId}`;
  return axiosWithAuth({ timeout: 30000 })
    .post(url, paymentDetails)
    .then((resp: any) => resp.data.data)
    .catch((error: any) => {
      console.error(error);
      if (appInsightCallback) {
        appInsightCallback({
          message: `Unexpected error while calling service: ${url}`,
          exception: error.toString(),
          severity: SEVERITY_LEVEL_ERROR,
        });
      }
      console.error('Error from MyCars - scheduleOneTimePayment Service');
      validateAuthResponse(!isLoggedIn);
      throw error;
    });
};

const cancelPayment = (env: string, vehicleId: string, paymentDetails: {}) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_CANCEL_PAYMENT_API_URI}${vehicleId}`;
  return axiosWithAuth().post(url, paymentDetails);
};

const saveAutopayEnrollment = (
  env: string,
  vehicleId: string,
  data?: any,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_SCHEDULE_AUTOPAY_METHOD_API_URI}${vehicleId}`;
  return myCarsPost(url, data, appInsightCallback);
};

const saveBankInfo = (
  env: string,
  vehicleId: string,
  data?: any,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_PAY_METHOD_ADD_API_URI}${vehicleId}`;
  return myCarsPost(url, data, appInsightCallback);
};

const myCarsPost = (
  url: string,
  data?: any,
  appInsightCallback?: (params: any) => void
) =>
  axiosWithAuth(true)
    .post(url, data)
    .then((resp: any) => resp.data)
    .catch((error: any) => {
      console.error(error);
      if (appInsightCallback) {
        appInsightCallback({
          message: `Unexpected error while calling service: ${url}`,
          exception: error.toString(),
          severity: SEVERITY_LEVEL_ERROR,
        });
      }
      console.error('Error from myCarsPost Service');
      validateAuthResponse(!isLoggedIn);
      return error;
    });

// GET Calls

const myCarsGet = (url: string, appInsightCallback?: (params: any) => void) =>
  axiosWithAuth(true)
    .get(url)
    .then((resp: any) => resp.data.data || resp.data)
    .catch((error: any) => {
      console.error(error);
      if (appInsightCallback) {
        appInsightCallback({
          message: `Unexpected error while calling service: ${url}`,
          exception: error.toString(),
          severity: SEVERITY_LEVEL_ERROR,
        });
      }
      console.error('Error from MyCars - getMyCars Service');
      validateAuthResponse(!isLoggedIn);
      throw error;
    });

const fetchMyCars = (
  env: string,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_API_URI}`;
  return myCarsGet(url, appInsightCallback);
};

const fetchCarLoanDetails = (
  env: string,
  vehicleId: string,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_LOAN_API_URI}${vehicleId}`;
  return myCarsGet(url, appInsightCallback);
};

const fetchCarBankInfo = (
  env: string,
  vehicleId: string,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_PAY_METHOD_API_URI}${vehicleId}`;
  return myCarsGet(url, appInsightCallback);
};

const fetchContactDetails = (
  env: string,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_CONTACT_DETAILS_API_URI}`;
  return myCarsGet(url, appInsightCallback);
};

const turnOffAutopay = (
  env: string,
  vehicleId: string,
  last4AccountNumber: string,
  appInsightsCallBack?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_DISABLE_AUTOPAY_API_URI}/${vehicleId}`;
  const data = { last4AccountNumber };
  return myCarsPost(url, data, appInsightsCallBack);
};

const fetchTransactionHistory = (
  env: string,
  vehicleId: string,
  appInsightCallback?: (params: any) => void
) => {
  const url: string = `${process.env.CARVANA_API_HYDRA_BASE_URL}${MY_CARS_TRANSACTION_HISTORY_API_URI}${vehicleId}`;
  return myCarsGet(url, appInsightCallback);
};

export {
  fetchMyCars,
  fetchCarLoanDetails,
  fetchCarBankInfo,
  saveBankInfo,
  cancelPayment,
  scheduleOneTimePayment,
  fetchContactDetails,
  saveAutopayEnrollment,
  turnOffAutopay,
  fetchTransactionHistory,
};
